import { Auth } from './auth';
import {
  getClassByDevice,
  hideNativeComponents,
  showNativeComponents,
} from './utils';

export interface IframeMessage<P = unknown> {
  source: string;
  action: string;
  payload: P;
}

export type WidgetListenerCallback<P> = (
  message: IframeMessage<P>,
) => void | Promise<void>;

export type WidgetListeners = {
  open: { accNumber: string };
  close: never;
};

export interface WidgetConfig<L extends WidgetListeners = WidgetListeners> {
  id: string;
  iframeId: string;
  iframeSourceId: string;
  hostSourceId: string;
  baseUrl: string;
  timeout: number;
  listeners: Partial<{
    [K in keyof L]: WidgetListenerCallback<L[K]>;
  }>;
}

export class Widget<
  L extends WidgetListeners = WidgetListeners,
  E extends object = object,
> {
  public config: WidgetConfig<L>;

  private isReady: boolean = false;
  private auth: Auth | null = null;
  private initialHash: string;

  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private loadResolvers: {
    resolve: (value: boolean) => void;
    reject: (reason?: Error) => void;
  } | null = null;

  private iframe: HTMLIFrameElement | null = null;
  private sbContainer: HTMLElement | null = null;
  private wrapper: HTMLDivElement | null = null;
  private spinner: HTMLDivElement | null = null;

  constructor(config: WidgetConfig<L>) {
    this.config = config;
    this.initialHash = window.location.hash;

    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.handleIframeMessage = this.handleIframeMessage.bind(this);
  }

  private disableBodyScroll() {
    document.body.classList.add('noScroll');
  }

  private enableBodyScroll() {
    document.body.classList.remove('noScroll');
  }

  private onOverlayClick({ target }: MouseEvent) {
    if (
      target instanceof HTMLElement &&
      !this.iframe?.contains(target) &&
      !this.spinner?.contains(target)
    ) {
      this.config.listeners.close?.({
        source: this.config.hostSourceId,
        action: 'close',
        payload: {} as L['close'],
      });
    }
  }

  private createSpinner() {
    const spinner = document.createElement('div');
    spinner.className = 'widget-modal__spinner';

    return spinner;
  }

  private createWrapper() {
    const wrapper = document.createElement('div');
    wrapper.className = 'widget-modal';

    return wrapper;
  }

  private createIframe() {
    const iframe = document.createElement('iframe');
    iframe.id = this.config.iframeId;
    iframe.src = this.config.baseUrl;
    iframe.referrerPolicy = 'origin';

    if (this.auth) {
      if (!this.auth.serviceTicket) {
        throw new Error('Service ticket is required for authentication');
      }

      const url = new URL(iframe.src);
      url.searchParams.set('st', this.auth.serviceTicket);

      iframe.src = url.toString();
    }

    return iframe;
  }

  private removeSpinner() {
    this.spinner?.remove();
    this.spinner = null;
  }

  private removeWrapper() {
    this.wrapper?.remove();
    this.wrapper = null;
  }

  private removeIframe() {
    this.iframe?.remove();
    this.iframe = null;
  }

  private async waitForIframeLoad() {
    return new Promise<boolean>((resolve, reject) => {
      this.loadResolvers = { resolve, reject };
      this.timeoutId = setTimeout(() => resolve(false), this.config.timeout);
    });
  }

  private onIframeLoad(message: IframeMessage<{ accNumber: string }>) {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    this.removeSpinner();

    if (this.auth && message.payload.accNumber !== this.auth.accountNumber) {
      this.loadResolvers?.reject(new Error('Account number mismatch'));
    } else {
      this.isReady = true;
      this.loadResolvers?.resolve(true);
    }

    this.loadResolvers = null;
  }

  private async handleIframeMessage(event: MessageEvent) {
    if (!this.iframe || typeof event.data !== 'object') {
      return;
    }

    if (event.data.source === this.config.iframeSourceId) {
      const message: IframeMessage<L[keyof L]> = event.data;
      const action = message.action as keyof L;

      if (action === 'open') {
        this.onIframeLoad(message as IframeMessage<{ accNumber: string }>);
      }

      const listener = this.config.listeners[action];

      if (listener) {
        try {
          await listener(message);
        } catch (error) {
          console.error(`"${message.action}":`, error);
        }
      } else if (action !== 'open') {
        console.warn(`Unsupported action: ${message.action}`);
      }
    }
  }

  public async postMessage<A extends keyof E>(action: A, payload: E[A]) {
    if (!this.iframe || !this.iframe.contentWindow || !this.isReady) {
      throw new Error('Widget iframe is not initialized');
    }

    this.iframe.contentWindow.postMessage(
      { source: this.config.hostSourceId, action, payload },
      this.config.baseUrl,
    );
  }

  public async start(selector: string, auth: Auth | null = null) {
    this.auth = auth;
    this.sbContainer = document.getElementById(selector);

    if (!this.sbContainer) {
      throw new Error(`Container "${selector}" not found`);
    }

    this.disableBodyScroll();

    this.sbContainer.classList.add(getClassByDevice());

    this.spinner = this.createSpinner();
    this.wrapper = this.createWrapper();
    this.iframe = this.createIframe();

    this.wrapper.addEventListener('click', this.onOverlayClick);

    this.wrapper.appendChild(this.spinner);
    this.wrapper.appendChild(this.iframe);
    this.sbContainer.appendChild(this.wrapper);

    window.addEventListener('message', this.handleIframeMessage);

    hideNativeComponents();

    const hasStarted = await this.waitForIframeLoad();

    if (!hasStarted) {
      throw new Error('Widget timed out');
    }
  }

  public stop() {
    this.enableBodyScroll();
    this.removeSpinner();
    this.removeWrapper();
    this.removeIframe();

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.isReady = false;
    this.auth = null;
    this.sbContainer = null;
    this.timeoutId = null;
    this.loadResolvers = null;
    window.location.hash = window.location.hash.replace(this.initialHash, '');

    window.removeEventListener('message', this.handleIframeMessage);

    showNativeComponents();
  }
}
